/**
 * Social session page: Log Game and Games tabs, plus player management.
 * Rendered by SessionDashboard when session type is Social Session.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../utils/supabase';
import Button from '../components/common/Button';
import SectionHeader from '../components/common/SectionHeader';
import SessionLayout from '../components/session/SessionLayout';
import EndSessionModal from '../components/session/EndSessionModal';
import LogGameForm from '../components/social/LogGameForm';
import SocialGamesList from '../components/social/SocialGamesList';
import SocialPlayerManager from '../components/players/SocialPlayerManager';
import AddExistingClubPlayerModal from '../components/players/AddExistingClubPlayerModal';
import SessionPlayersList from '../components/players/SessionPlayersList';
import usePlayerManagement from '../hooks/usePlayerManagement';
import useCourtManagement from '../hooks/useCourtManagement';
import { useSeason } from '../context/SeasonContext';
import { updateSessionStatus, createGame, addPlayersToSession } from '../services/sessionService';
import { useSessionScoringConfig } from '../hooks/useSessionScoringConfig';

function SocialSessionPage({ currentUser, selectedClub, sessionId, session, isSessionHost, sessionCreator = null, isClubAdmin = false }) {
  const navigate = useNavigate();
  const { season, loading: seasonLoading } = useSeason();
  const scoringConfig = useSessionScoringConfig(session);

  const [activeTab, setActiveTab] = useState('logGame');
  const [socialCourtPlayers, setSocialCourtPlayers] = useState([]);
  const [showEndSessionModal, setShowEndSessionModal] = useState(false);
  const [endingSession, setEndingSession] = useState(false);
  const [endSessionError, setEndSessionError] = useState(null);
  const [gamesRefreshKey, setGamesRefreshKey] = useState(0);
  const [clubRole, setClubRole] = useState(null);
  const [showAddPlayerModal, setShowAddPlayerModal] = useState(false);

  const {
    players = [],
    playersById = {},
    activePlayers = [],
    togglePlayerStatus,
    bulkToggleStatus,
    loading: playersLoading,
    setPlayerSessionStatus,
    fetchPlayers,
    fetchSessionPlayerStatus,
    sessionPlayerStatus = {},
    sessionPlayerExitReasons = {},
  } = usePlayerManagement(selectedClub?.id, sessionId, season?.id);

  const playersByIdWithGames = playersById;

  const socialSessionActivePlayers = players.filter((p) => sessionPlayerStatus[p.id] !== undefined);
  const socialCourtPlayersList = socialCourtPlayers;
  const socialActivePlayers = socialSessionActivePlayers.filter(
    (player) => !socialCourtPlayers.some((cp) => cp.playerId === player.id)
  );

  const {
    numberOfCourts,
    setNumberOfCourts,
    courtAssignments: _courtAssignments,
    waitingList: _waitingList,
    setWaitingList: _setWaitingList,
  } = useCourtManagement({
    players,
    activePlayers,
    sessionId,
    updatePlayers: () => {},
    playersById: playersByIdWithGames,
    sessionPlayerStatus,
    fetchPlayers,
    seasonId: season?.id,
    partnerships: [],
    isSocialSession: true,
    clubId: selectedClub?.id,
    setPlayerSessionStatus,
    scheduledGames: [],
    gameTypeRestriction: 'all_types',
    playerCategories: null,
  });

  // Load full club player list so "Available" tab can show club members not yet in the session
  useEffect(() => {
    if (selectedClub?.id && sessionId) {
      fetchPlayers();
    }
  }, [selectedClub?.id, sessionId, fetchPlayers]);

  useEffect(() => {
    if (session?.number_of_courts != null && session.number_of_courts >= 1) {
      setNumberOfCourts(session.number_of_courts);
    }
  }, [session?.number_of_courts, setNumberOfCourts]);

  useEffect(() => {
    if (!currentUser?.id || !selectedClub?.id) {
      setClubRole(null);
      return;
    }
    supabase
      .from('players_club')
      .select('club_role')
      .eq('player_id', currentUser.id)
      .eq('club_id', selectedClub.id)
      .single()
      .then(({ data }) => setClubRole(data?.club_role || null));
  }, [currentUser?.id, selectedClub?.id]);

  const isAdmin =
    currentUser?.role === 'platform_admin' ||
    currentUser?.role === 'club_owner' ||
    clubRole === 'admin';

  // Show player management to session host or club admins/owners/hosts
  const canAccessPlayerManagement =
    isSessionHost || isClubAdmin || clubRole === 'host' || isAdmin;

  const playersOnCourtsSet = useMemo(
    () => new Set(socialCourtPlayers.map((cp) => cp.playerId)),
    [socialCourtPlayers]
  );

  const getSocialCourtStructure = useCallback(() => {
    const courts = [];
    for (let i = 1; i <= numberOfCourts; i++) {
      const courtPlayers = socialCourtPlayers.filter((cp) => cp.courtNumber === i);
      courts.push({
        courtNumber: i,
        team1: courtPlayers.filter((cp) => cp.teamNumber === 1).map((cp) => cp.playerId),
        team2: courtPlayers.filter((cp) => cp.teamNumber === 2).map((cp) => cp.playerId),
        inProgress: false,
        winner: null,
        score: null,
      });
    }
    return courts;
  }, [numberOfCourts, socialCourtPlayers]);

  const handleAddPlayerToSocialCourt = useCallback(
    (playerId, courtNumber, teamNumber) => {
      setSocialCourtPlayers((prev) => [...prev, { playerId, courtNumber, teamNumber }]);
    },
    []
  );

  const handleRemovePlayerFromSocialCourt = useCallback((playerId, team, courtNumber) => {
    setSocialCourtPlayers((prev) => prev.filter((cp) => cp.playerId !== playerId));
  }, []);

  const handleSocialSessionUpdateMatchResult = useCallback(
    async (courtIndex, score, testMode = false) => {
      const court = getSocialCourtStructure()[courtIndex];
      if (!court) return;
      try {
        const gameData = {
          sessionId,
          clubId: selectedClub?.id,
          team_1_score: Number(score?.team1Score) || 0,
          team_2_score: Number(score?.team2Score) || 0,
          winningTeam: score.winningTeam,
          startTime: new Date().toISOString(),
          endTime: new Date().toISOString(),
          team1Players: court.team1,
          team2Players: court.team2,
          season_id: season?.id,
        };
        await createGame(gameData, playersById, testMode);
        const courtNumber = courtIndex + 1;
        setSocialCourtPlayers((prev) => prev.filter((cp) => cp.courtNumber !== courtNumber));
        setGamesRefreshKey((k) => k + 1);
        fetchPlayers();
      } catch (err) {
        console.error('Error in social session updateMatchResult:', err);
      }
    },
    [
      getSocialCourtStructure,
      sessionId,
      selectedClub?.id,
      season?.id,
      playersById,
      fetchPlayers,
    ]
  );

  const handleEndSessionPlayerStates = useCallback(async () => {
    const result = await bulkToggleStatus('end_session');
    if (!result.success) throw new Error(result.error || 'Failed to update player states');
  }, [bulkToggleStatus]);

  const handleSessionEnd = useCallback(
    async (status) => {
      if (!sessionId) return;
      setEndingSession(true);
      setEndSessionError(null);
      try {
        const normalizedStatus = status.toLowerCase();
        await handleEndSessionPlayerStates();
        await updateSessionStatus(sessionId, normalizedStatus);
        if (normalizedStatus === 'completed' || normalizedStatus === 'cancelled') {
          navigate(`/club/${selectedClub?.id}/sessions/review`);
        }
      } catch (err) {
        console.error('Error ending session:', err);
        setEndSessionError(`Failed to update session: ${err.message || 'Unknown error'}`);
      } finally {
        setEndingSession(false);
      }
    },
    [sessionId, selectedClub?.id, navigate, handleEndSessionPlayerStates]
  );

  const handleTogglePlayerStatus = useCallback(
    async (playerId, targetStatus, exitReason) => {
      if (targetStatus === 'remove' && playersOnCourtsSet.has(playerId)) {
        return false;
      }
      const result = await togglePlayerStatus(playerId, targetStatus, exitReason);
      if (!result.success) {
        return false;
      }
      if (targetStatus === 'remove' || result.newStatus === undefined) {
        setSocialCourtPlayers((prev) => prev.filter((cp) => cp.playerId !== playerId));
      }
      return true;
    },
    [togglePlayerStatus, playersOnCourtsSet]
  );

  const handlePasteRosterAccept = useCallback(async (writableRows) => {
    const toAdd = (writableRows || [])
      .map((row) => row.selectedPlayerId)
      .filter((playerId) => playerId && sessionPlayerStatus[playerId] === undefined);
    const alreadyCount = (writableRows || []).length - toAdd.length;

    if (!toAdd.length) {
      return { addedCount: 0, alreadyCount };
    }
    if (!sessionId || !selectedClub?.id) {
      throw new Error('Session or club is missing');
    }

    await addPlayersToSession(sessionId, toAdd, selectedClub.id, 'signed_up');
    await fetchSessionPlayerStatus({ silent: true });
    return { addedCount: toAdd.length, alreadyCount };
  }, [sessionId, selectedClub?.id, sessionPlayerStatus, fetchSessionPlayerStatus]);

  if (playersLoading && players.length === 0) {
    return (
      <div className="relative font-sans bg-background text-text-primary max-w-full mx-auto px-3 py-3 min-h-screen">
        <div className="flex items-center justify-center py-12">
          <div className="text-xl font-semibold text-primary animate-pulse">Loading session...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="relative font-sans bg-background text-text-primary min-h-screen">
      <div className="max-w-full mx-auto py-3">
        <div className="px-3 sm:px-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between mb-3">
            <div className="flex flex-wrap items-center gap-2 sm:space-x-3">
              <h2 className="text-2xl font-bold text-text-primary">Social Session Dashboard</h2>
              <span className="bg-success-light text-text-primary border border-border-medium text-sm font-medium px-3 py-1 rounded-full">
                🏸 Social Session
              </span>
            </div>
            <div className="text-base sm:text-lg font-semibold text-text-secondary">
              {seasonLoading ? 'Loading season...' : season ? `Season: ${season.name}` : 'No active season'}
            </div>
          </div>

          <SessionLayout
            sessionName={session?.name || 'Session Dashboard'}
            sessionCreator={sessionCreator}
            endSessionButton={
              isSessionHost ? (
                <Button onClick={() => setShowEndSessionModal(true)} variant="danger">
                  End Session
                </Button>
              ) : null
            }
          />

          <div className="mb-6 p-4 bg-success-light border border-border-medium rounded-lg">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-success" viewBox="0 0 20 20" fill="currentColor" aria-hidden>
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-text-primary">🏸 Social Session Mode</h3>
                <p className="text-sm text-text-secondary mt-1">
                  This is a social session with no auto-matchmaking. Players can manually join courts
                  and track games if they choose to.
                </p>
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-4 items-center mb-2">
            <Button
              onClick={() => setActiveTab('logGame')}
              variant={activeTab === 'logGame' ? 'primary' : 'ghost'}
              size="medium"
              className="rounded-full"
            >
              Log Game
            </Button>
            <Button
              onClick={() => setActiveTab('games')}
              variant={activeTab === 'games' ? 'primary' : 'ghost'}
              size="medium"
              className="rounded-full"
            >
              Games
            </Button>
          </div>

          <div className="relative">
            {playersLoading && (
              <div className="absolute inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-[1px]">
                <div className="text-xl font-semibold text-primary animate-pulse">
                  Updating players...
                </div>
              </div>
            )}

            {activeTab === 'logGame' ? (
              <div className="mt-6 space-y-6">
                {!canAccessPlayerManagement && (
                  <SessionPlayersList
                    players={players}
                    sessionPlayerStatus={sessionPlayerStatus}
                    sessionPlayerExitReasons={sessionPlayerExitReasons}
                  />
                )}
                <LogGameForm
                  sessionId={sessionId}
                  clubId={selectedClub?.id}
                  seasonId={season?.id}
                  currentUserId={currentUser?.id}
                  signedUpPlayers={players.filter((p) => sessionPlayerStatus[p.id] === 'active')}
                  playersById={playersById}
                  onGameLogged={() => {
                    setGamesRefreshKey((k) => k + 1);
                    fetchPlayers();
                  }}
                  canLogWithoutPlaying={canAccessPlayerManagement}
                  scoringConfig={scoringConfig}
                />
                {canAccessPlayerManagement && (
                  <div>
                    <SectionHeader title="Player Management" variant="primary" />
                    <hr className="border-t border-border-medium mb-4" />
                    <SocialPlayerManager
                      players={players}
                      sessionPlayerStatus={sessionPlayerStatus}
                      sessionPlayerExitReasons={sessionPlayerExitReasons}
                      togglePlayerStatus={handleTogglePlayerStatus}
                      playersOnActiveCourts={playersOnCourtsSet}
                      currentClubId={selectedClub?.id}
                      sessionId={sessionId}
                      onGuestUpdated={fetchPlayers}
                      onAddNewPlayer={() => setShowAddPlayerModal(true)}
                    />
                  </div>
                )}
              </div>
            ) : activeTab === 'games' ? (
              <div className="mt-6">
                <SocialGamesList
                  sessionId={sessionId}
                  playersById={playersByIdWithGames}
                  currentUserId={currentUser?.id}
                  refreshKey={gamesRefreshKey}
                  scoringConfig={scoringConfig}
                  onGameDeleted={() => {
                    setGamesRefreshKey((k) => k + 1);
                    fetchPlayers();
                  }}
                />
              </div>
            ) : null}
          </div>
        </div>
      </div>

      <AddExistingClubPlayerModal
        isOpen={showAddPlayerModal}
        onClose={() => setShowAddPlayerModal(false)}
        players={players}
        sessionPlayerStatus={sessionPlayerStatus}
        sessionEntryStatus="signed_up"
        clubId={selectedClub?.id}
        seasonId={season?.id}
        onAddToSession={async (playerId) => handleTogglePlayerStatus(playerId, 'signed_up')}
        onPasteRoster={handlePasteRosterAccept}
        currentUser={currentUser}
      />

      <EndSessionModal
        isOpen={showEndSessionModal}
        onClose={() => setShowEndSessionModal(false)}
        onEndSession={handleSessionEnd}
        isLoading={endingSession}
        error={endSessionError}
      />
    </div>
  );
}

export default SocialSessionPage;
